import { toast } from 'sonner'
import { create } from 'zustand'

import {
  type BookAiPurpose,
  createPurposePlan,
  createPurposePractice,
  deletePurpose,
  getPurpose,
  getSectionPurposeQuestions,
  type PurposeOrigin,
  putPurpose,
} from '@/api/services/book-ai-purpose'

/**
 * リーダーで開いている本の「この本を読む目的」と、目的に合わせて作ったもの（利用者ごと・本ごと）
 *
 * 一度に開く本は 1 冊なので、episodeId が変わったら中身を入れ替える。
 * 応答が返る前に別の本へ移った場合は、古い応答を捨てる。
 */
type BookAiPurposeStore = {
  episodeId: number | null
  purpose: BookAiPurpose | null
  isLoaded: boolean
  isSaving: boolean
  isPlanning: boolean
  isPracticing: boolean
  /** 目的に沿った問いを作っている章の番号 */
  loadingQuestions: number[]
  open: (episodeId: number) => Promise<void>
  reload: () => Promise<void>
  /** origin: AI と磨いて決めたときの、最初に書いた目的と答えた状況（省くと今のまま） */
  save: (purpose: string, origin?: PurposeOrigin) => Promise<boolean>
  remove: () => Promise<void>
  requestPlan: () => Promise<void>
  requestPractice: (untilKey: number | null) => Promise<void>
  loadSectionQuestions: (sectionIndex: number) => Promise<void>
}

export const useBookAiPurposeStore = create<BookAiPurposeStore>((set, get) => {
  /** 今の本のまま応答が返ったときだけ反映する */
  const run = async <T>(task: (episodeId: number) => Promise<T>, apply: (result: T) => void, failure: string) => {
    const episodeId = get().episodeId
    if (episodeId == null) return false
    try {
      const result = await task(episodeId)
      if (get().episodeId !== episodeId) return false
      apply(result)
      return true
    } catch (error) {
      console.error(`${failure}:`, error)
      if (get().episodeId === episodeId) toast.error(failure)
      return false
    }
  }

  const updatePurpose = (patch: Partial<BookAiPurpose>) =>
    set((state) => ({ purpose: state.purpose ? { ...state.purpose, ...patch } : state.purpose }))

  return {
    episodeId: null,
    purpose: null,
    isLoaded: false,
    isSaving: false,
    isPlanning: false,
    isPracticing: false,
    loadingQuestions: [],

    open: async (episodeId) => {
      if (get().episodeId === episodeId) return
      set({
        episodeId,
        purpose: null,
        isLoaded: false,
        isSaving: false,
        isPlanning: false,
        isPracticing: false,
        loadingQuestions: [],
      })
      await get().reload()
    },

    reload: async () => {
      await run(getPurpose, (purpose) => set({ purpose, isLoaded: true }), '読む目的を取得できませんでした')
    },

    save: async (text, origin) => {
      set({ isSaving: true })
      const saved = await run(
        (episodeId) => putPurpose(episodeId, text, origin),
        (purpose) => set({ purpose }),
        '読む目的を保存できませんでした',
      )
      set({ isSaving: false })
      // 読み方の提案は目的を決めたら続けて作る（目的ごとに 1 回）
      if (saved && !get().purpose?.plan) void get().requestPlan()
      return saved
    },

    remove: async () => {
      await run(deletePurpose, () => set({ purpose: null }), '読む目的を消せませんでした')
    },

    requestPlan: async () => {
      if (get().isPlanning) return
      set({ isPlanning: true })
      await run(
        createPurposePlan,
        (plan) => updatePurpose({ plan, plan_outdated: false }),
        '読み方の提案を作れませんでした',
      )
      set({ isPlanning: false })
    },

    requestPractice: async (untilKey) => {
      if (get().isPracticing) return
      set({ isPracticing: true })
      await run(
        (episodeId) => createPurposePractice(episodeId, untilKey),
        (practice) => updatePurpose({ practice }),
        '実践メモを作れませんでした',
      )
      set({ isPracticing: false })
    },

    loadSectionQuestions: async (sectionIndex) => {
      const { purpose, loadingQuestions } = get()
      if (!purpose || loadingQuestions.includes(sectionIndex)) return
      if (Object.hasOwn(purpose.section_questions, String(sectionIndex))) return
      set({ loadingQuestions: [...loadingQuestions, sectionIndex] })
      await run(
        (episodeId) => getSectionPurposeQuestions(episodeId, sectionIndex),
        (questions) =>
          set((state) => ({
            purpose: state.purpose && {
              ...state.purpose,
              section_questions: { ...state.purpose.section_questions, [String(sectionIndex)]: questions },
            },
          })),
        '目的に沿った問いを作れませんでした',
      )
      set((state) => ({ loadingQuestions: state.loadingQuestions.filter((i) => i !== sectionIndex) }))
    },
  }
})

/** 開いている本の読む目的（別の本の目的が残っているときや未設定なら null） */
export const useCurrentPurpose = (episodeId: number): BookAiPurpose | null =>
  useBookAiPurposeStore((state) => (state.episodeId === episodeId ? state.purpose : null))
